/**
 * Universal DUEL QR Poster — not gym-specific.
 * QR points to app.douevenlift.com; users pick their gym during onboarding.
 * Printable via window.print() with @media print styles.
 */

import { View, Text, StyleSheet, ScrollView, Platform, TouchableOpacity, Image as RNImage } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { Printer } from 'lucide-react-native';

const APP_URL = 'https://app.douevenlift.com';
const QR_API = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&color=B8972E&bgcolor=0A0A0A&data=${encodeURIComponent(APP_URL)}`;

export default function GymQRScreen() {
  const { theme } = useTheme();
  const c = theme.colors;

  const handlePrint = () => {
    if (Platform.OS === 'web') {
      (window as any).print();
    }
  };

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: '#0A0A0A' }}
      contentContainerStyle={styles.container}
    >
      {/* Print styles injected for web */}
      {Platform.OS === 'web' && (
        <style
          dangerouslySetInnerHTML={{
            __html: `
              @media print {
                body { margin: 0 !important; background: #0A0A0A !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                [data-no-print] { display: none !important; }
              }
            `,
          }}
        />
      )}

      <View style={styles.poster}>
        {/* Header */}
        <Text style={styles.headline}>THE COMPETITIVE STRENGTH LEAGUE</Text>
        <Text style={styles.subHeadline}>STRENGTH • COMPETITION • CAMARADERIE</Text>
        <View style={styles.divider} />

        {/* QR Code */}
        <View style={styles.qrContainer}>
          {Platform.OS === 'web' ? (
            // @ts-ignore web-only
            <img src={QR_API} alt="DUEL QR Code" style={{ width: 260, height: 260 }} />
          ) : (
            <RNImage source={{ uri: QR_API }} style={{ width: 260, height: 260 }} accessibilityLabel="DUEL QR Code" />
          )}
        </View>

        {/* CTA */}
        <Text style={styles.cta}>SCAN TO JOIN</Text>
        <Text style={styles.subCta}>Track your lifts. Challenge your rivals.</Text>
        <Text style={styles.subCta}>Prove you belong on the board.</Text>

        {/* URL fallback */}
        <View style={styles.urlBar}>
          <Text style={styles.urlText}>{APP_URL.replace('https://', '')}</Text>
        </View>

        {/* Brand */}
        <View style={styles.brandRow}>
          <Text style={styles.brandName}>DUEL</Text>
          <Text style={styles.brandTag}>PROVE IT. TRACK IT. OWN IT.</Text>
        </View>
      </View>

      {/* Print button (hidden in print) */}
      <TouchableOpacity
        style={styles.printButton}
        onPress={handlePrint}
        // @ts-ignore web only
        dataSet={{ noPrint: true }}
        data-no-print="true"
      >
        <Printer size={20} color="#0A0A0A" />
        <Text style={styles.printText}>PRINT POSTER</Text>
      </TouchableOpacity>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    padding: 24,
    minHeight: '100%',
  } as any,
  poster: {
    backgroundColor: '#0A0A0A',
    borderWidth: 2,
    borderColor: '#B8972E',
    borderRadius: 0,
    padding: 40,
    alignItems: 'center',
    maxWidth: 400,
    width: '100%',
  } as any,
  headline: {
    fontFamily: 'Oswald_700Bold',
    fontSize: 36,
    color: '#B8972E',
    letterSpacing: 4,
    textAlign: 'center',
    marginBottom: 8,
  },
  subHeadline: {
    fontFamily: 'BarlowCondensed_700Bold',
    fontSize: 13,
    color: '#B8972E',
    letterSpacing: 3,
    textAlign: 'center',
    textTransform: 'uppercase',
    marginBottom: 12,
  },
  divider: {
    width: 80,
    height: 3,
    backgroundColor: '#B8972E',
    marginBottom: 28,
  },
  qrContainer: {
    backgroundColor: '#0A0A0A',
    padding: 16,
    borderWidth: 1,
    borderColor: '#2A2A2A',
    borderRadius: 2,
    marginBottom: 28,
  },
  cta: {
    fontFamily: 'Oswald_700Bold',
    fontSize: 28,
    color: '#E8E4DB',
    letterSpacing: 3,
    textAlign: 'center',
    marginBottom: 8,
  },
  subCta: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: '#7A776E',
    textAlign: 'center',
    lineHeight: 22,
  },
  urlBar: {
    marginTop: 24,
    backgroundColor: '#1A1A1A',
    paddingVertical: 8,
    paddingHorizontal: 20,
    borderRadius: 2,
  },
  urlText: {
    fontFamily: 'BarlowCondensed_700Bold',
    fontSize: 16,
    color: '#B8972E',
    letterSpacing: 2,
    textTransform: 'uppercase',
  },
  brandRow: {
    marginTop: 32,
    alignItems: 'center',
    gap: 4,
  },
  brandName: {
    fontFamily: 'Oswald_700Bold',
    fontSize: 22,
    color: '#E8E4DB',
    letterSpacing: 6,
  },
  brandTag: {
    fontFamily: 'BarlowCondensed_700Bold',
    fontSize: 11,
    color: '#4A4840',
    letterSpacing: 4,
    textTransform: 'uppercase',
  },
  printButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#B8972E',
    paddingVertical: 14,
    paddingHorizontal: 28,
    borderRadius: 0,
    marginTop: 24,
  },
  printText: {
    fontFamily: 'Oswald_700Bold',
    fontSize: 16,
    color: '#0A0A0A',
    letterSpacing: 2,
  },
});
